import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import * as DOMPurify from 'dompurify';
import { ConversationMessage, InboxItem } from './types';
import { TYPE_META, companyInitials, IconButton } from './InboxCard';
import appState from '../../state/AppStateContainer';
import { ServiceProjects } from '../../services/service-projects';
import { ServiceDownload } from '../../services/service-download';
import { FileScope } from '../../util/appEnum';
import BtButton from '../bt-button/bt-button';
import styles from './InboxPreviewPanel.module.scss';

// Render the message cleanly: drop the email CTA <center> block and all inline
// styles/classes so email-template spacing doesn't bloat the compact preview.
const sanitizeHTML = (html: string): string => {
  const stripped = html.replace(/<center[\s\S]*?<\/center>/gi, '');
  return DOMPurify.sanitize(stripped, { FORBID_ATTR: ['style', 'class'] });
};

// Pull the CTA link out of the email's orange button (getOrangeButton) so we can
// re-render it as a proper bt-button without keeping the email styling.
const extractCta = (html: string): { href: string; text: string } | null => {
  const m = html.match(/background-color:\s*#FF5C00[\s\S]*?<a\s+href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/i);
  if (!m) return null;
  const text = m[2].replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  return { href: m[1], text };
};


type InboxPreviewPanelProps = {
  item: InboxItem | null;
  onClose: () => void;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onCreateActivity: () => void;
  onReplySent: () => void;
};

const InboxPreviewPanel: FC<InboxPreviewPanelProps> = ({
  item, onClose, onMarkRead, onMarkUnread, onCreateActivity, onReplySent,
}) => {
  const [t] = useTranslation('dashboard');
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [conversation, setConversation] = useState<ConversationMessage[] | null>(null);
  const [convLoading, setConvLoading] = useState(false);
  const [newMsgIds, setNewMsgIds] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const anchorElRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pendingAnchorScrollRef = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  // Close on click outside the panel — uses mousedown so scroll events are unaffected.
  useEffect(() => {
    if (!item) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onCloseRef.current();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [item]);

  // Auto-mark-read after a short dwell, so arrowing PAST a conversation doesn't
  // mark it read (and fire a mark-read PUT + notification refetch) on transit —
  // only landing on one for ~half a second does. Changing selection (or closing)
  // cancels the pending timer via the cleanup.
  useEffect(() => {
    if (!item || item.unreadCount === 0) return;
    const id = setTimeout(() => onMarkRead(), 500);
    return () => clearTimeout(id);
  }, [item?.conversationId]);

  useEffect(() => {
    setReplyText('');
    setReplyFiles([]);
  }, [item?.conversationId]);

  useEffect(() => {
    if (!item) { setConversation(null); return; }
    // Capture unread state before onMarkRead mutates it, so we can mark "new" messages.
    const wasUnread = item.unreadCount > 0;
    const latestId = item.latestMsgId;
    setConversation(null);
    setNewMsgIds(new Set());
    setConvLoading(true);
    ServiceProjects.getConversation(item.supplierId, item.projectId, item.supplierMemberId, item.areaId)
      .then((data: { conversation: ConversationMessage[] }) => {
        if (data?.conversation) {
          // API returns newest-first; reverse for chronological display
          const conv = [...data.conversation].reverse();
          pendingAnchorScrollRef.current = true;
          setConversation(conv);
          const ids = new Set(conv.filter(m => m.isUnread).map(m => m._id));
          if (wasUnread && latestId) ids.add(latestId);
          setNewMsgIds(ids);
        }
      })
      .catch(() => {})
      .finally(() => setConvLoading(false));
  }, [item?.conversationId]);

  // On open, jump (no animation) to the new message. Skipped after a reply
  // refetch so the post-send scroll-to-bottom isn't overridden.
  useEffect(() => {
    if (conversation && pendingAnchorScrollRef.current) {
      pendingAnchorScrollRef.current = false;
      (anchorElRef.current ?? bottomRef.current)?.scrollIntoView({ block: 'center' });
    }
  }, [conversation]);

  const orgUrl = appState._getCurrentOrgUrl();

  const handleSend = async () => {
    if (!item || (!replyText.trim() && replyFiles.length === 0)) return;
    setSending(true);
    try {
      await ServiceProjects.postAdminMessage(
        item.projectId,
        item.supplierId,
        item.supplierMemberId,
        replyText,
        replyFiles,
        item.areaId,
      );
      setReplyText('');
      setReplyFiles([]);
      // Fire the inbox-list refresh and the conversation reload in parallel — both
      // are independent reads after the write has completed.
      onReplySent();
      const data = await ServiceProjects.getConversation(item.supplierId, item.projectId, item.supplierMemberId, item.areaId);
      if (data?.conversation) setConversation([...data.conversation].reverse());
      // Show the just-sent reply at the bottom.
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);
    } catch {
      // keep form on error
    } finally {
      setSending(false);
    }
  };

  return (
    <div ref={panelRef} className={`${styles.panel} ${item ? styles.panelOpen : ''}`}>
        {item && (
          <>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerMain}>
                <div className={styles.headerTitleRow}>
                  <span className={styles.headerName}>
                    {item.supplierName || item.title}
                  </span>
                  {(() => {
                    const typeInfo = TYPE_META[item.messageType] ?? TYPE_META.standardMessage;
                    return (
                      <span className={styles.headerTypeBadge} style={{ backgroundColor: typeInfo.bg, color: typeInfo.color }}>
                        {t(typeInfo.labelKey, typeInfo.fallback)}
                      </span>
                    );
                  })()}
                </div>
                <div className={styles.headerSub}>
                  {item.title && <span>{item.title}</span>}
                  {item.title && item.senderName && <span> · </span>}
                  {item.senderName && <span className={styles.headerSender}>{item.senderName}</span>}
                </div>
              </div>
              <div className={styles.headerActions}>
                <IconButton
                  onClick={item.unreadCount === 0 ? onMarkUnread : onMarkRead}
                  icon={item.unreadCount === 0 ? MailOutlineIcon : DraftsOutlinedIcon}
                  title={item.unreadCount === 0 ? t('inbox.markAsUnread', 'Markera som oläst') : t('inbox.markAsRead', 'Markera som läst')}
                  size={36} borderRadius={8} iconSize={18}
                />
                <IconButton onClick={onCreateActivity} icon={AddTaskOutlinedIcon} title={t('inbox.createActivity', 'Skapa aktivitet')} size={36} borderRadius={8} iconSize={18} />
                <button onClick={onClose} title={t('inbox.preview.close', 'Stäng')} className={styles.closeBtn}>
                  ×
                </button>
              </div>
            </div>

            {/* Conversation thread */}
            <div className={styles.thread}>
              {convLoading && (
                <div className={styles.convLoading}>
                  {t('inbox.preview.loadingConversation', 'Laddar konversation...')}
                </div>
              )}
              {!convLoading && conversation && conversation.map((msg) => {
                const isAdmin = !!msg.adminId;
                const senderName = msg.overrideInformation?.senderName
                  || msg.adminId?.name
                  || msg.supplierMemberId?.name
                  || '';
                const initials = companyInitials(senderName);
                const isNew = newMsgIds.has(msg._id);
                const isAnchor = msg._id === item.latestMsgId;
                const cta = extractCta(msg.message);
                const ctaTo = cta && !/^https?:\/\//i.test(cta.href) ? `${orgUrl}${cta.href}` : cta?.href;
                const ctaLabel = cta ? (cta.text || t('inbox.preview.open', 'Öppna')) : '';
                const scopeOptions = {
                  projectId: item.projectId,
                  supplierId: item.supplierId,
                  areaId: item.areaId,
                  clientId: item.supplierMemberId,
                };
                return (
                  <div
                    key={msg._id}
                    ref={isAnchor ? anchorElRef : undefined}
                    className={`${styles.msg} ${isAdmin ? styles.msgAdmin : ''}`}
                  >
                    <div className={styles.msgHead}>
                      <div className={`${styles.msgAvatar} ${isAdmin ? styles.msgAvatarAdmin : ''}`}>
                        {initials}
                      </div>
                      <span className={styles.msgSender}>{senderName}</span>
                      <div className={styles.msgMeta}>
                        {isNew && (
                          <span title={t('inbox.preview.new', 'Ny')} className={styles.newDot} />
                        )}
                        <span className={styles.msgTime}>
                          {moment(msg.createdAt).format('D MMM YYYY, HH:mm')}
                        </span>
                      </div>
                    </div>
                    <div
                      className={styles.messageBody}
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(msg.message) }}
                    />
                    {cta && ctaTo && (
                      /^https?:\/\//i.test(cta.href) ? (
                        <a href={ctaTo} target="_blank" rel="noreferrer" className={styles.ctaLink}>
                          <BtButton size="small">{ctaLabel}</BtButton>
                        </a>
                      ) : (
                        <Link to={ctaTo} className={styles.ctaLink}>
                          <BtButton size="small">{ctaLabel}</BtButton>
                        </Link>
                      )
                    )}
                    {msg.attachment?.length > 0 && (
                      <div className={styles.attachList}>
                        {msg.attachment.map((att, j) => {
                          const ext = att.fileName?.split('.').pop() || '';
                          return (
                            <button
                              key={j}
                              onClick={() => ServiceDownload.downloadFile(att.file, ext, att.fileName, FileScope.ProjectConversation, scopeOptions)}
                              className={styles.attachBtn}
                            >
                              <AttachFileIcon style={{ fontSize: 13, color: '#9ca3af' }} />
                              {att.fileName || att.file}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Reply */}
            <div className={styles.reply}>
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder={t('inbox.preview.replyPlaceholder', 'Skriv ett svar...')}
                rows={3}
                className={styles.replyTextarea}
              />
              {replyFiles.length > 0 && (
                <div className={styles.replyFiles}>
                  {replyFiles.map((f, i) => (
                    <span key={i} className={styles.replyFileChip}>
                      {f.name}
                      <button
                        onClick={() => setReplyFiles(prev => prev.filter((_, j) => j !== i))}
                        className={styles.replyFileRemove}
                      >
                        <CloseIcon style={{ fontSize: 12 }} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className={styles.replyActions}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  title={t('inbox.preview.attach', 'Bifoga fil')}
                  className={styles.attachButton}
                >
                  <AttachFileIcon style={{ fontSize: 16 }} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className={styles.hiddenInput}
                  onChange={e => {
                    if (e.target.files) setReplyFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                    e.target.value = '';
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={sending || (!replyText.trim() && replyFiles.length === 0)}
                  className={styles.sendBtn}
                  style={sending ? { cursor: 'wait' } : undefined}
                >
                  <SendIcon style={{ fontSize: 15 }} />
                  {sending ? t('inbox.preview.sending', 'Skickar...') : t('inbox.preview.send', 'Skicka')}
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className={styles.footer}>
              <Link
                to={`${orgUrl}/projects/${item.projectSlug}/${item.areaSlug}/${item.supplierSlug}/${item.supplierMemberSlug}`}
                className={styles.footerLink}
              >
                {t('inbox.goToConversation', 'Gå till konversation')} →
              </Link>
            </div>
          </>
        )}
    </div>
  );
};

export default InboxPreviewPanel;
